import { Box, Button, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const ENHANCIFY_PROMO_URL =
  "https://www.enhancify.com/home-design-guild-2-promo";

export const DesignerAddOnOffer = ({
  compact = false,
  useMembershipCardShell = false,
}: {
  compact?: boolean;
  useMembershipCardShell?: boolean;
}) => {
  const headingSize = useMembershipCardShell
    ? "1.5rem"
    : compact
    ? "1.7rem"
    : "2rem";
  const oldPriceSize = useMembershipCardShell
    ? "1.25rem"
    : compact
    ? "1.35rem"
    : "1.55rem";
  const priceSize = useMembershipCardShell
    ? "1.5rem"
    : compact
    ? "2rem"
    : "2.25rem";
  const billingSize = useMembershipCardShell
    ? "0.875rem"
    : compact
    ? "1.25rem"
    : "1.45rem";

  const offerItems = [
    "Enhancify Professional Growth Plan",
    "Co-Branded Client Financing Portal",
    "30+ Lender Network Access",
    "Up to $200K Financing Per Project",
    "Earn Up to $650 Per Funded Project",
    "Dedicated Account Manager",
  ];

  return (
    <Box
      sx={{
        position: "relative",
        mt: compact ? 3 : 0,
        border: useMembershipCardShell
          ? "1px solid rgba(28, 28, 28, 0.4)"
          : "2px solid #C73D78",
        borderRadius: useMembershipCardShell ? "8px" : "14px",
        p: { xs: 2.5, md: 3.5 },
        pt: { xs: 6, md: 6.5 },
        background: useMembershipCardShell ? "#fff" : "#F1F1F1",
        minHeight: compact ? "auto" : useMembershipCardShell ? "100%" : 670,
        height: useMembershipCardShell ? "100%" : "auto",
        width: "100%",
        maxWidth: compact || useMembershipCardShell ? "none" : "360px",
        cursor: "default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translate(-50%, -50%)",
          px: 2.5,
          py: 0.7,
          borderRadius: "0 0 8px 8px",
          backgroundColor: "#C52D67",
          color: "#fff",
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          fontSize: "0.86rem",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        Add-On Offer
      </Box>

      <Stack spacing={1.25} sx={{ flexGrow: 1 }}>
        <Typography
          sx={{
            color: "#C52D67",
            fontWeight: 700,
            fontSize: headingSize,
            lineHeight: 1.1,
            mt: 0.5,
          }}
        >
          Homeowner Financing
        </Typography>

        <Typography
          sx={{
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "#4AA57E",
            fontWeight: 700,
            fontSize: useMembershipCardShell ? "0.875rem" : compact ? "0.92rem" : "0.98rem",
          }}
        >
          Powered by Enhancify
        </Typography>

        <Box sx={{ mt: 0.5 }}>
          <Typography
            sx={{
              color: "#9AA0A6",
              textDecoration: "line-through",
              fontWeight: 600,
              fontSize: oldPriceSize,
              lineHeight: 1.1,
            }}
          >
            $1,749.00
          </Typography>
          <Typography
            sx={{
              color: "#111827",
              fontWeight: 700,
              fontSize: priceSize,
              lineHeight: 1.05,
            }}
          >
            $1,500.00
          </Typography>
          <Typography
            sx={{
              color: "#4B5563",
              fontWeight: useMembershipCardShell ? 400 : 600,
              fontSize: billingSize,
              lineHeight: 1.1,
            }}
          >
            Billed Annually
          </Typography>
        </Box>

        <Stack
          component="ul"
          className={useMembershipCardShell ? "subscription-feature-list" : undefined}
          sx={{
            listStyle: "none",
            p: 0,
            m: 0,
            mt: 1.2,
            gap: useMembershipCardShell ? 1.5 : 1.2,
          }}
        >
          {offerItems.map((item) => (
            <Box
              key={item}
              component="li"
              className={useMembershipCardShell ? "subscription-feature-item" : undefined}
              sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}
            >
              <CheckCircleIcon
                className={useMembershipCardShell ? "subscription-feature-icon" : undefined}
                sx={{
                  color: useMembershipCardShell ? "#0DAB76" : "#4AA57E",
                  fontSize: useMembershipCardShell ? "20px" : "1.7rem",
                  width: useMembershipCardShell ? "20px" : undefined,
                  height: useMembershipCardShell ? "20px" : undefined,
                  mt: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography
                className={useMembershipCardShell ? "subscription-feature-copy" : undefined}
                sx={{
                  color: "#4B5563",
                  fontWeight: useMembershipCardShell ? 400 : 600,
                  fontSize: useMembershipCardShell ? "1rem" : compact ? "1rem" : "1.12rem",
                  lineHeight: useMembershipCardShell ? 1.6 : 1.24,
                }}
              >
                {item}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Typography
          sx={{
            color: "#9CA3AF",
            fontSize: compact ? "0.88rem" : "0.93rem",
            fontWeight: 600,
            mt: 0.7,
            mb: useMembershipCardShell ? 0 : 0.7,
          }}
        >
          Financing offers vary by lender and applicant.
        </Typography>
      </Stack>

      <Box sx={{ pt: 1.5, mt: "auto" }}>
        <Button
          variant="contained"
          href={ENHANCIFY_PROMO_URL}
          target="_blank"
          rel="noopener noreferrer"
          fullWidth
          sx={{
            textTransform: "uppercase",
            letterSpacing: "0.03em",
            fontWeight: 700,
            py: 1.2,
            borderRadius: "8px",
            fontSize: compact ? "0.95rem" : "0.98rem",
            backgroundColor: "#C52D67",
            "&:hover": { backgroundColor: "#B22459" },
          }}
        >
          Learn More About Homeowner Financing
        </Button>
      </Box>
    </Box>
  );
};
